import axiosInstance from './axios';

export type PaymentMethod = 'EFECTIVO' | 'TARJETA' | 'QR' | 'TRANSFERENCIA' | 'DEBITO' | 'CREDITO';

export interface Product {
    id: string;
    nombre: string;
    codigo?: string | null;
    categoria?: string | null;
    descripcion?: string | null;
    precio: number;
    controlaStock: boolean;
    activo: boolean;
    /** Existencia en la sede activa (o suma de todas en el consolidado) */
    stock: number;
    stockMinimo?: number;
    stocks?: { sedeId: string; cantidad: number; stockMinimo: number; sede?: { id: string; nombre: string } }[];
}

export interface ProductData {
    nombre: string;
    codigo?: string;
    categoria?: string;
    descripcion?: string;
    precio: number;
    controlaStock?: boolean;
    activo?: boolean;
}

export interface StockData {
    cantidad?: number;
    ajuste?: number;
    stockMinimo?: number;
    motivo?: string;
}

export interface SaleItem {
    id: string;
    productId: string;
    nombre: string;
    cantidad: number;
    precioUnitario: number;
    subtotal: number;
}

export interface Sale {
    id: string;
    numero: string;
    fecha: string;
    total: number;
    metodoPago: PaymentMethod;
    status: 'COMPLETADA' | 'ANULADA';
    observaciones?: string | null;
    sedeId?: string | null;
    shiftId?: string | null;
    items: SaleItem[];
    sede?: { id: string; nombre: string } | null;
    operador?: { nombre: string };
}

export interface StockAdjustment {
    id: string;
    ajuste: number;
    cantidadResultante: number;
    motivo?: string | null;
    createdAt: string;
    sede?: { nombre: string };
}

export interface StoreStats {
    totalVentas: number;
    cantidadVentas: number;
    porMetodo: { metodoPago: PaymentMethod; total: number }[];
    porSede: { sedeId: string | null; sede: string; total: number; cantidad: number }[];
    topProductos: { productId: string; nombre: string; cantidad: number; total: number }[];
    stockBajo: { producto: string; sede: string | null; cantidad: number; stockMinimo: number }[];
}

export interface SaleFilters {
    fechaInicio?: string;
    fechaFin?: string;
    shiftId?: string;
}

export const getProducts = async (includeInactive = false): Promise<Product[]> => {
    const response = await axiosInstance.get<Product[]>('/store/products', {
        params: includeInactive ? { includeInactive: 'true' } : undefined,
    });
    return response.data;
};

export const createProduct = async (data: ProductData): Promise<Product> => {
    const response = await axiosInstance.post<Product>('/store/products', data);
    return response.data;
};

export const updateProduct = async (id: string, data: Partial<ProductData>): Promise<Product> => {
    const response = await axiosInstance.put<Product>(`/store/products/${id}`, data);
    return response.data;
};

export const deleteProduct = async (id: string): Promise<{ message: string }> => {
    const response = await axiosInstance.delete<{ message: string }>(`/store/products/${id}`);
    return response.data;
};

export const updateStock = async (id: string, data: StockData) => {
    const response = await axiosInstance.put(`/store/products/${id}/stock`, data);
    return response.data;
};

export const getStockAdjustments = async (id: string): Promise<StockAdjustment[]> => {
    const response = await axiosInstance.get<StockAdjustment[]>(`/store/products/${id}/adjustments`);
    return response.data;
};

export const getSales = async (filters?: SaleFilters): Promise<Sale[]> => {
    const response = await axiosInstance.get<Sale[]>('/store/sales', { params: filters });
    return response.data;
};

export const createSale = async (data: {
    items: { productId: string; cantidad: number }[];
    metodoPago?: PaymentMethod;
    observaciones?: string;
}): Promise<Sale> => {
    const response = await axiosInstance.post<Sale>('/store/sales', data);
    return response.data;
};

export const cancelSale = async (id: string): Promise<{ message: string }> => {
    const response = await axiosInstance.post<{ message: string }>(`/store/sales/${id}/cancel`);
    return response.data;
};

export const getStoreStats = async (filters?: SaleFilters): Promise<StoreStats> => {
    const response = await axiosInstance.get<StoreStats>('/store/stats', { params: filters });
    return response.data;
};

export default {
    getProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    updateStock,
    getStockAdjustments,
    getSales,
    createSale,
    cancelSale,
    getStoreStats,
};
